/**
 * DocumentHistoryPanel.jsx — Единен таймлайн на всички качени документи.
 *
 * Изтегля `/api/documents/history` и показва подредено по дата:
 *   • Епикризи        (Stethoscope, кехлибарен)
 *   • Лаборатории     (FlaskConical, син)
 *   • DUTCH           (FlaskConical, лилав)
 *   • ДНК             (Dna, индиго)
 *
 * За всяка епикриза:
 *   - Резюме, лекар, дата
 *   - Chip-предложения от AI (диагнози → KEGG пътища)
 *   - "Преглед" отваря оригиналния файл
 *   - "Изтрий" с confirm
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Stethoscope, FlaskConical, Dna, FileText, Trash2, Eye, Loader2,
  ChevronDown, ChevronUp, Sparkles, CheckCircle2, ExternalLink, AlertCircle,
  Droplets,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { emitHealthEvent } from "@/utils/healthEvents";
import { toast } from "sonner";
import LabImpactPanel from "@/components/breast_intel/LabImpactPanel";

const TX = {
  bg: {
    title: "📚 История на документи",
    subtitle: "Всичко, което сте качили — на едно място. Кликнете за детайли или изтрийте с потвърждение.",
    empty: "Все още няма качени документи. Използвайте бутона „Качи документ“ по-горе.",
    show: "Покажи",
    hide: "Скрий",
    view: "Преглед",
    delete: "Изтрий",
    confirmDelete: "Сигурни ли сте, че искате да изтриете този документ?",
    deleted: "Документът е изтрит",
    deleteError: "Грешка при изтриване",
    loading: "Зареждаме…",
    doctor: "Лекар",
    diagnoses: "Диагнози в документа",
    suggestedTitle: "🧬 AI-предложени диагнози → KEGG пътища",
    suggestedIntro: "AI разпозна тези диагнози в текста и ги свърза с метаболитно/възпалителни сигнални пътища. Приемете, за да участват в BHI изчисленията.",
    accepted: "Приета",
    acceptAll: "Приеми всички",
    accept: "Приеми",
    accepting: "Записваме…",
    acceptError: "Грешка при приемане",
    acceptSuccess: "Диагнозите са добавени в профила Ви",
    pathwaysLabel: "пътища",
    findings: "маркера",
    of: "от",
    kegg: "KEGG",
  },
  en: {
    title: "📚 Document history",
    subtitle: "Everything you have uploaded — one place. Click for details or delete with confirmation.",
    empty: "No documents yet. Use the upload button above.",
    show: "Show",
    hide: "Hide",
    view: "View",
    delete: "Delete",
    confirmDelete: "Are you sure you want to delete this document?",
    deleted: "Document deleted",
    deleteError: "Delete failed",
    loading: "Loading…",
    doctor: "Doctor",
    diagnoses: "Diagnoses in the document",
    suggestedTitle: "🧬 AI-suggested diagnoses → KEGG pathways",
    suggestedIntro: "AI detected these diagnoses in the text and linked them to inflammatory/metabolic signalling pathways. Accept to include them in BHI.",
    accepted: "Accepted",
    acceptAll: "Accept all",
    accept: "Accept",
    accepting: "Saving…",
    acceptError: "Accept failed",
    acceptSuccess: "Diagnoses added to your profile",
    pathwaysLabel: "pathways",
    findings: "markers",
    of: "of",
    kegg: "KEGG",
  },
};

const TYPE_META = {
  epicrisis:    { Icon: Stethoscope, color: "text-amber-500",  ring: "ring-amber-500/30",  bg: "bg-amber-500/10" },
  standard_lab: { Icon: FlaskConical, color: "text-blue-500",   ring: "ring-blue-500/30",   bg: "bg-blue-500/10"  },
  dutch:        { Icon: FlaskConical, color: "text-purple-500", ring: "ring-purple-500/30", bg: "bg-purple-500/10"},
  genomic:      { Icon: Dna,          color: "text-indigo-500", ring: "ring-indigo-500/30", bg: "bg-indigo-500/10"},
};

function formatDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("bg-BG", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

export default function DocumentHistoryPanel({ lang = "bg", refreshKey = 0 }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null); // "id::delete" | "id::accept"
  // iter887 — panel-level collapsible state (default expanded, persisted)
  const [panelOpen, setPanelOpen] = useState(() => {
    try {
      const v = localStorage.getItem("bhi:doc-history:open");
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("bhi:doc-history:open", panelOpen ? "1" : "0"); } catch { /* noop */ }
  }, [panelOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/documents/history`);
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    const onUploaded = () => load();
    window.addEventListener("bhi:document:uploaded", onUploaded);
    return () => window.removeEventListener("bhi:document:uploaded", onUploaded);
  }, [load]);

  const handleDelete = async (it) => {
    if (!window.confirm(t.confirmDelete)) return;
    setBusy(`${it.id}::delete`);
    try {
      if (it.type === "epicrisis") {
        await axios.delete(`${API}/documents/epicrises/${it.id}`);
      } else if (it.type === "standard_lab" && it.filename) {
        await axios.delete(`${API}/health/lab-analyses/${encodeURIComponent(it.filename)}`);
      } else {
        toast.error(t.deleteError);
        setBusy(null);
        return;
      }
      toast.success(t.deleted);
      setItems((cur) => cur.filter((x) => !(x.id === it.id && x.type === it.type)));
      emitHealthEvent("conditions-changed", { source: "document-deleted" });
    } catch (e) {
      toast.error(t.deleteError);
    } finally {
      setBusy(null);
    }
  };

  const handleView = async (it) => {
    try {
      let url;
      if (it.type === "epicrisis") {
        const res = await axios.get(
          `${API}/documents/epicrises/${it.id}/file`,
          { responseType: "blob" },
        );
        url = URL.createObjectURL(res.data);
      } else if (it.type === "standard_lab" && it.filename) {
        const res = await axios.get(
          `${API}/health/lab-analyses/${encodeURIComponent(it.filename)}/pdf`,
          { responseType: "blob" },
        );
        url = URL.createObjectURL(res.data);
      } else {
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(t.deleteError);
    }
  };

  const acceptConditions = async (it, condition_ids) => {
    setBusy(`${it.id}::accept`);
    try {
      const { data } = await axios.post(
        `${API}/documents/epicrises/${it.id}/accept-conditions`,
        condition_ids ? { condition_ids } : {},
      );
      toast.success(`${t.acceptSuccess} (${data.count})`);
      // Optimistic UI update
      setItems((cur) =>
        cur.map((x) =>
          x.id === it.id && x.type === "epicrisis"
            ? { ...x, accepted_condition_ids: data.accepted_condition_ids || [] }
            : x,
        ),
      );
      emitHealthEvent("conditions-changed", { source: "epicrisis-accept" });
    } catch (e) {
      toast.error(t.acceptError);
    } finally {
      setBusy(null);
    }
  };

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  return (
    <div className={`${cardCls} p-5 mb-4`} data-testid="document-history-panel">
      <button
        type="button"
        onClick={() => setPanelOpen((v) => !v)}
        className="w-full flex items-start gap-3 text-left group"
        aria-expanded={panelOpen}
        data-testid="doc-history-collapse-toggle"
      >
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-extrabold">{t.title}</h3>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-xs font-bold ${isDark ? "text-blue-300" : "text-blue-600"}`}>
                {items.length}
              </span>
              {panelOpen ? (
                <ChevronUp className={`w-4 h-4 ${isDark ? "text-gray-400" : "text-gray-500"} group-hover:opacity-100 opacity-70`} />
              ) : (
                <ChevronDown className={`w-4 h-4 ${isDark ? "text-gray-400" : "text-gray-500"} group-hover:opacity-100 opacity-70`} />
              )}
            </div>
          </div>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{t.subtitle}</p>
        </div>
      </button>

      {!panelOpen ? null : loading ? (
        <div className="py-10 text-center text-sm opacity-70" data-testid="doc-history-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> {t.loading}
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm opacity-70" data-testid="doc-history-empty">{t.empty}</div>
      ) : (
        <ul className="space-y-2" data-testid="doc-history-list">
          {items.map((it) => {
            const meta = TYPE_META[it.type] || TYPE_META.standard_lab;
            const key = `${it.type}-${it.id}`;
            const isOpen = !!expanded[key];
            const suggestedRaw = Array.isArray(it.suggested_conditions) ? it.suggested_conditions : [];
            const accepted = new Set(it.accepted_condition_ids || []);
            const pendingCount = suggestedRaw.filter((s) => !accepted.has(s.condition_id)).length;
            return (
              <li
                key={key}
                className={`rounded-xl border ${isDark ? "border-white/10" : "border-gray-200"}`}
                data-testid={`doc-item-${it.type}-${it.id}`}
              >
                <div className="p-3 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                    <meta.Icon className={`w-4 h-4 ${meta.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold truncate">{it.type_label_bg}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                        isDark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"
                      }`}>{formatDate(it.date || it.created_at)}</span>
                      {it.type === "epicrisis" && pendingCount > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-300">
                          {pendingCount} нови диагнози
                        </span>
                      )}
                      {it.type === "standard_lab" && (() => {
                        const sc = it.suggested_conditions || [];
                        const acc = new Set(it.accepted_condition_ids || []);
                        const p = sc.filter((s) => !acc.has(s.condition_id)).length;
                        if (p === 0) return null;
                        return (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-600 dark:text-pink-300 inline-flex items-center gap-0.5">
                            <Droplets className="w-2.5 h-2.5" /> {p} сигнала за биологията
                          </span>
                        );
                      })()}
                    </div>
                    {it.doctor && (
                      <div className="text-[11px] opacity-70 mt-0.5">
                        <b>{t.doctor}:</b> {it.doctor}
                      </div>
                    )}
                    {it.filename && (
                      <div className="text-[11px] opacity-60 truncate">{it.filename}</div>
                    )}
                    {it.summary_bg && (
                      <p className={`text-xs mt-1 leading-snug line-clamp-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        {it.summary_bg}
                      </p>
                    )}
                    {typeof it.meta?.findings_count === "number" && it.meta.findings_count > 0 && (
                      <div className="text-[11px] opacity-70 mt-0.5">
                        {it.meta.findings_count} {t.findings}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {it.can_view && (
                      <button
                        onClick={() => handleView(it)}
                        className={`px-2 py-1 rounded-md text-[10px] font-semibold flex items-center gap-1 ${
                          isDark ? "bg-white/5 hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200"
                        }`}
                        data-testid={`doc-view-${it.id}`}
                        title={t.view}
                      >
                        <Eye className="w-3 h-3" /> {t.view}
                      </button>
                    )}
                    {it.can_delete && (
                      <button
                        onClick={() => handleDelete(it)}
                        disabled={busy === `${it.id}::delete`}
                        className="px-2 py-1 rounded-md text-[10px] font-semibold text-red-500 bg-red-500/10 hover:bg-red-500/20 flex items-center gap-1 disabled:opacity-60"
                        data-testid={`doc-delete-${it.id}`}
                        title={t.delete}
                      >
                        {busy === `${it.id}::delete` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        {t.delete}
                      </button>
                    )}
                    {((it.type === "epicrisis" && suggestedRaw.length > 0) ||
                      (it.type === "standard_lab" && (it.has_impact || (it.suggested_conditions || []).length > 0))) && (
                      <button
                        onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}
                        className="px-2 py-1 rounded-md text-[10px] font-semibold text-blue-500 bg-blue-500/10 hover:bg-blue-500/20 flex items-center gap-1"
                        data-testid={`doc-toggle-${it.id}`}
                      >
                        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {isOpen ? t.hide : t.show}
                      </button>
                    )}
                  </div>
                </div>

                {/* iter883 — lab items: on expand, show LabImpactPanel */}
                {it.type === "standard_lab" && isOpen && (
                  <div className={`px-3 pb-3 pt-1 border-t ${isDark ? "border-white/5" : "border-gray-100"}`}>
                    <LabImpactPanel labId={it.id} lang={lang} compact />
                  </div>
                )}

                {/* Expanded — suggested conditions → KEGG pathways */}
                {it.type === "epicrisis" && isOpen && (
                  <div className={`px-3 pb-3 pt-1 border-t ${isDark ? "border-white/5" : "border-gray-100"}`}>
                    {Array.isArray(it.documented_diagnoses) && it.documented_diagnoses.length > 0 && (
                      <div className="mb-3 text-xs">
                        <div className="font-bold opacity-80">{t.diagnoses}:</div>
                        <div className="opacity-75">{it.documented_diagnoses.join(", ")}</div>
                      </div>
                    )}
                    {suggestedRaw.length === 0 ? null : (
                      <>
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-1.5">
                            <Sparkles className="w-4 h-4 text-pink-500" />
                            <span className="text-xs font-bold">{t.suggestedTitle}</span>
                          </div>
                          {pendingCount > 1 && (
                            <button
                              onClick={() => acceptConditions(it, null)}
                              disabled={busy === `${it.id}::accept`}
                              className="px-2.5 py-1 rounded-md text-[10px] font-bold text-white bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90 disabled:opacity-60 flex items-center gap-1"
                              data-testid={`doc-accept-all-${it.id}`}
                            >
                              {busy === `${it.id}::accept` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="w-3 h-3" />
                              )}
                              {t.acceptAll}
                            </button>
                          )}
                        </div>
                        <p className="text-[11px] opacity-70 leading-relaxed mb-2">{t.suggestedIntro}</p>
                        <ul className="space-y-2">
                          {suggestedRaw.map((s) => {
                            const isAccepted = accepted.has(s.condition_id);
                            return (
                              <li
                                key={s.condition_id}
                                className={`p-2.5 rounded-lg border text-xs ${
                                  isAccepted
                                    ? "border-emerald-500/40 bg-emerald-500/10"
                                    : (isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50")
                                }`}
                                data-testid={`doc-suggested-${it.id}-${s.condition_id}`}
                              >
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <span className="font-bold truncate">{s.name_bg}</span>
                                    {isAccepted ? (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 dark:text-emerald-300 font-bold shrink-0">
                                        <CheckCircle2 className="w-3 h-3" /> {t.accepted}
                                      </span>
                                    ) : null}
                                  </div>
                                  {!isAccepted && (
                                    <button
                                      onClick={() => acceptConditions(it, [s.condition_id])}
                                      disabled={busy === `${it.id}::accept`}
                                      className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-pink-500/20 hover:bg-pink-500/30 text-pink-600 dark:text-pink-300 disabled:opacity-60 flex items-center gap-1 shrink-0"
                                      data-testid={`doc-accept-${it.id}-${s.condition_id}`}
                                    >
                                      {busy === `${it.id}::accept` ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <CheckCircle2 className="w-3 h-3" />
                                      )}
                                      {t.accept}
                                    </button>
                                  )}
                                </div>
                                {Array.isArray(s.matching_pathways) && s.matching_pathways.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {s.matching_pathways.map((p) => (
                                      <a
                                        key={p.id}
                                        href={p.kegg ? `https://www.kegg.jp/pathway/${p.kegg}` : undefined}
                                        target={p.kegg ? "_blank" : undefined}
                                        rel="noopener noreferrer"
                                        className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                          isDark ? "bg-white/10 hover:bg-white/15 text-blue-200" : "bg-blue-50 hover:bg-blue-100 text-blue-700"
                                        }`}
                                        title={p.name_bg}
                                      >
                                        {p.name_bg}
                                        {p.kegg && <ExternalLink className="w-2.5 h-2.5 opacity-60" />}
                                      </a>
                                    ))}
                                  </div>
                                )}
                                {(!s.matching_pathways || s.matching_pathways.length === 0) && (
                                  <div className="flex items-center gap-1 text-[10px] opacity-60">
                                    <AlertCircle className="w-3 h-3" />
                                    Няма мапирани KEGG пътища за тази диагноза
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
